import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useGroups, useTasks } from '../../hooks/useCrmData';
import type { SearchResult } from '../../types/crm';
import { COLORS, Icons, StatCard, Spinner, ErrorBlock, EmptyBlock } from '../ui/crm-ui';

interface Props {
  baseApiUrl: string;
  userFirstName?: string;
}

function fileExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

function todayHumanDate(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const DashboardView: React.FC<Props> = ({ baseApiUrl, userFirstName }) => {
  const groups = useGroups(baseApiUrl);
  const tasks = useTasks(baseApiUrl);

  // Recent documents — pulled via /api/documents/search?q= using a wildcard-ish query.
  // The backend only returns results when `q` is non-empty, so we send a single space
  // which Graph treats as a broad match. If that returns nothing we render the empty state.
  const [recentDocs, setRecentDocs] = useState<SearchResult[] | null>(null);
  const [recentErr, setRecentErr] = useState<string | null>(null);

  useEffect(() => {
    if (!baseApiUrl) return;
    let cancelled = false;
    axios
      .get<SearchResult[]>(`${baseApiUrl}/api/documents/search`, { params: { q: ' ' } })
      .then((res) => {
        if (cancelled) return;
        const sorted = [...res.data].sort((a, b) => {
          const da = a.lastModified ? new Date(a.lastModified).getTime() : 0;
          const db = b.lastModified ? new Date(b.lastModified).getTime() : 0;
          return db - da;
        });
        setRecentDocs(sorted.slice(0, 4));
      })
      .catch((err) => {
        if (cancelled) return;
        setRecentErr(err instanceof Error ? err.message : String(err));
      });
    return () => { cancelled = true; };
  }, [baseApiUrl]);

  const openTasks = (tasks.data?.tasks || []).filter((t) => t.status !== 'completed' && t.status !== 'cancelled');
  const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
  const overdue = openTasks.filter((t) => {
    if (!t.due_date) return false;
    return new Date(t.due_date) < todayMidnight;
  });

  const totalGroups = groups.data?.length ?? 0;
  // Total documents is not a single API field. We approximate with the recent-docs result count
  // when available, and fall back to "—" when search isn't returning anything.
  const totalDocs = recentDocs == null ? null : recentDocs.length > 0 ? `${recentDocs.length}+` : '0';

  return (
    <div className="crm-anim-fadeUp">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
          {greeting()}{userFirstName ? `, ${userFirstName}` : ''}
        </h1>
        <p style={{ fontSize: 13, color: COLORS.mid, margin: '4px 0 0' }}>{todayHumanDate()}</p>
      </div>

      <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard
          label="Open Tasks"
          value={tasks.loading ? '…' : openTasks.length}
          sub={overdue.length > 0 ? `${overdue.length} overdue` : undefined}
          accent={overdue.length > 0 ? COLORS.red : undefined}
        />
        <StatCard
          label="Overdue"
          value={tasks.loading ? '…' : overdue.length}
          accent={overdue.length > 0 ? COLORS.red : undefined}
        />
        <StatCard
          label="Client Groups"
          value={groups.loading ? '…' : totalGroups}
        />
        <StatCard
          label="Recent Documents"
          value={totalDocs ?? '…'}
        />
      </div>

      <div>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Recent Documents</div>
        {recentErr && <ErrorBlock message={recentErr} />}
        {!recentErr && recentDocs == null && <Spinner />}
        {!recentErr && recentDocs?.length === 0 && (
          <EmptyBlock>No recent documents available yet.</EmptyBlock>
        )}
        {!recentErr && recentDocs && recentDocs.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {recentDocs.map((d, i) => {
              const ext = fileExt(d.name);
              const date = d.lastModified ? new Date(d.lastModified).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
              const parent = d.parentPath.split('/').filter(Boolean).slice(-1)[0] || '';
              return (
                <div
                  key={d.id}
                  className="crm-anim-fadeUp"
                  style={{
                    background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.border}`,
                    padding: 16, animationDelay: `${i * 0.06}s`, animationFillMode: 'both',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    {Icons.file(ext)}
                    <span style={{ fontSize: 13, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.name}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.mid }}>{parent}</div>
                  <div style={{ fontSize: 11, color: COLORS.mid, marginTop: 4 }}>{date}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardView;
